/* eslint-disable comma-dangle */
import { FetchApi } from "@/utils/FetchApi";

export const NOA_RECORDER_LIFECYCLE_FLAG_KEY = "NOA_recorder_lifecycle_tracing_enabled";

export const RECORDER_LIFECYCLE_EVENT_TYPES = [
  "modal_opened",
  "modal_hidden",
  "iframe_load_started",
  "iframe_loaded",
  "iframe_error",
  "iframe_recreated",
  "recorder_ready",
  "recording_started",
  "recording_finished",
  "document_visibility_changed",
  "page_lifecycle",
  "component_unmounted_during_recording",
  "interrupted_observed",
] as const;

export type RecorderLifecycleEventType = (typeof RECORDER_LIFECYCLE_EVENT_TYPES)[number];
export type RecordingStartedSource = "iframe_post_message" | "backend_status_fallback";

export type RecentRecorderLifecycleEvent = {
  eventType: RecorderLifecycleEventType;
  occurredAt?: string;
  ageMs?: number;
};

export type RecorderLifecyclePayload = {
  eventType: RecorderLifecycleEventType;
  occurredAt: string;
  sequence: number;
  pluginInstanceId: string;
  iframeInstanceId?: string;
  source: "ai-assistant-plugin-v2-frontend";
  stateMachineState?: string;
  recordingActive?: boolean;
  recorderVisible?: boolean;
  iframePresent?: boolean;
  iframeLoaded?: boolean;
  iframeLoading?: boolean;
  iframeErrored?: boolean;
  documentVisibility?: string;
  pagePath?: string;
  navigationKind?: string;
  startedSource?: RecordingStartedSource;
  elapsedMsSinceRecordingStarted?: number;
  recentEventTypes?: RecentRecorderLifecycleEvent[];
  metadata?: Record<string, string | number | boolean | null>;
};

export type LifecycleDeliveryOptions = {
  keepalive?: boolean;
};

export type LifecycleStateSnapshot = Pick<
  RecorderLifecyclePayload,
  "stateMachineState" | "recorderVisible" | "iframePresent" | "iframeLoaded" | "iframeLoading" | "iframeErrored"
>;

export type LifecycleSender = (
  recorderSessionId: string,
  payload: RecorderLifecyclePayload,
  delivery?: LifecycleDeliveryOptions
) => Promise<void>;

export type RecorderLifecycleTelemetryOptions = {
  isEnabled: () => boolean;
  getRecorderSessionId: () => string | null | undefined;
  send: LifecycleSender;
  getStateSnapshot?: () => LifecycleStateSnapshot;
  getPagePath?: () => string;
  now?: () => number;
  storage?: Storage;
  windowRef?: Window;
  documentRef?: Document;
};

type MutableHistoryMethod = "pushState" | "replaceState";

const SOURCE = "ai-assistant-plugin-v2-frontend";
const MAX_PAGE_PATH_LENGTH = 512;
const MAX_RECENT_EVENTS = 10;
const STORAGE_PREFIX = "noa.recorder.lifecycle";
const PLUGIN_INSTANCE_ID_KEY = `${STORAGE_PREFIX}.pluginInstanceId`;
const SEQUENCE_KEY = `${STORAGE_PREFIX}.sequence`;
const RECORDING_ACTIVE_KEY = `${STORAGE_PREFIX}.recordingActive`;
const RECORDING_STARTED_AT_KEY = `${STORAGE_PREFIX}.recordingStartedAt`;
const RECORDER_SESSION_ID_KEY = `${STORAGE_PREFIX}.recorderSessionId`;
const RECENT_EVENTS_KEY = `${STORAGE_PREFIX}.recentEvents`;
const IFRAME_INSTANCE_ID_KEY = `${STORAGE_PREFIX}.iframeInstanceId`;

const sensitiveSegmentPrefixes = new Set([
  "patient",
  "patients",
  "paciente",
  "pacientes",
  "attendance",
  "attendances",
  "appointment",
  "appointments",
  "booking",
  "bookings",
  "consumer",
  "consumers",
  "episode",
  "episodes",
  "user",
  "users",
]);

export function sanitizeLifecyclePagePath(rawPath?: string): string {
  const fallbackOrigin = "https://app.feegow.com";
  const pathInput = rawPath || (typeof window !== "undefined" && window.location ? window.location.pathname : "/");

  let pathname = "/";

  try {
    pathname = new URL(pathInput, fallbackOrigin).pathname || "/";
  } catch {
    pathname = pathInput.split("?")[0].split("#")[0] || "/";
  }

  const sanitizedSegments = pathname
    .split("/")
    .filter((segment, index) => segment !== "" || index === 0)
    .map((segment, index, segments) => {
      if (segment === "") return "";

      const previousSegment = (segments[index - 1] || "").toLowerCase();
      if (sensitiveSegmentPrefixes.has(previousSegment) || isIdentifierLike(segment)) {
        return ":id";
      }

      return segment;
    });

  const sanitized = sanitizedSegments.join("/") || "/";
  return sanitized.length > MAX_PAGE_PATH_LENGTH ? sanitized.substring(0, MAX_PAGE_PATH_LENGTH) : sanitized;
}

export function createRecorderLifecycleApiSender(apiClient: FetchApi): LifecycleSender {
  return async (recorderSessionId, payload, delivery) => {
    await apiClient.fetch<string>(
      "POST",
      `/session/${encodeURIComponent(recorderSessionId)}/lifecycle-events`,
      payload,
      "text",
      { keepalive: delivery?.keepalive }
    );
  };
}

export class NoaRecorderLifecycleTelemetry {
  private readonly options: RecorderLifecycleTelemetryOptions;
  private readonly now: () => number;
  private readonly storage: Storage | null;
  private readonly windowRef?: Window;
  private readonly documentRef?: Document;
  private readonly pluginInstanceId: string;
  private readonly iframeInstanceId: string;
  private sequence: number;
  private recordingActive: boolean;
  private recordingStartedAt: number;
  private startedSource: RecordingStartedSource | null = null;
  private activeLifecycleCleanup: (() => void) | null = null;
  private recentEvents: RecentRecorderLifecycleEvent[];
  private readonly interruptedObservedSessions = new Set<string>();

  constructor(options: RecorderLifecycleTelemetryOptions) {
    this.options = options;
    this.now = options.now || Date.now;
    this.storage = options.storage || getDefaultStorage();
    this.windowRef = options.windowRef || (typeof window !== "undefined" ? window : undefined);
    this.documentRef = options.documentRef || (typeof document !== "undefined" ? document : undefined);
    this.pluginInstanceId = getOrCreateStoredValue(this.storage, PLUGIN_INSTANCE_ID_KEY, createInstanceId);
    this.iframeInstanceId = getOrCreateStoredValue(this.storage, IFRAME_INSTANCE_ID_KEY, createInstanceId);
    this.sequence = toNumber(this.storage?.getItem(SEQUENCE_KEY), 0);
    this.recordingActive = this.storage?.getItem(RECORDING_ACTIVE_KEY) === "true";
    this.recordingStartedAt = toNumber(this.storage?.getItem(RECORDING_STARTED_AT_KEY), 0);
    this.recentEvents = readRecentEvents(this.storage);
  }

  async emit(
    eventType: RecorderLifecycleEventType,
    overrides: Partial<RecorderLifecyclePayload> = {},
    delivery?: LifecycleDeliveryOptions
  ): Promise<void> {
    if (!this.options.isEnabled()) return;

    const recorderSessionId = this.options.getRecorderSessionId();
    if (!recorderSessionId) return;

    persistRecorderSessionId(this.storage, recorderSessionId);

    const occurredAtTime = this.now();
    const occurredAt = new Date(occurredAtTime).toISOString();
    const payload = this.buildPayload(eventType, occurredAt, overrides);

    this.rememberRecentEvent(eventType, occurredAt, occurredAtTime);

    try {
      await this.options.send(recorderSessionId, payload, delivery);
    } catch {
      // Best-effort observability must never affect the doctor's recording flow.
    }
  }

  async markRecordingStarted(source: RecordingStartedSource): Promise<void> {
    const shouldEmit = !this.recordingActive || this.startedSource !== source;

    this.recordingActive = true;
    this.startedSource = source;
    if (!this.recordingStartedAt) {
      this.recordingStartedAt = this.now();
    }
    this.persistRecordingState();
    this.startActiveLifecycleTracking();

    if (shouldEmit) {
      await this.emit("recording_started", { startedSource: source });
    }
  }

  async markRecordingFinished(): Promise<void> {
    if (!this.recordingActive) return;

    await this.emit("recording_finished");
    this.stopRecordingTracking();
  }

  async emitInterruptedObserved(): Promise<void> {
    const recorderSessionId = this.options.getRecorderSessionId();
    if (!recorderSessionId || this.interruptedObservedSessions.has(recorderSessionId)) return;

    this.interruptedObservedSessions.add(recorderSessionId);
    await this.emit("interrupted_observed");
    this.stopRecordingTracking();
  }

  async emitUnmountedIfRecording(delivery?: LifecycleDeliveryOptions): Promise<void> {
    if (!this.recordingActive) return;

    await this.emit("component_unmounted_during_recording", {}, delivery);
    this.stopRecordingTracking();
  }

  async trackNavigation(navigationKind: string, delivery?: LifecycleDeliveryOptions): Promise<void> {
    if (!this.recordingActive) return;

    await this.emit("page_lifecycle", { navigationKind }, delivery);
  }

  async trackDocumentVisibility(): Promise<void> {
    if (!this.recordingActive) return;

    await this.emit("document_visibility_changed", {
      documentVisibility: this.documentRef?.visibilityState,
      navigationKind: "visibilitychange",
    });
  }

  isRecordingActive(): boolean {
    return this.recordingActive;
  }

  cleanup(): void {
    this.cleanupActiveLifecycleTracking();
  }

  private buildPayload(
    eventType: RecorderLifecycleEventType,
    occurredAt: string,
    overrides: Partial<RecorderLifecyclePayload>
  ): RecorderLifecyclePayload {
    const sequenceNumber = this.nextSequence();
    const stateSnapshot = this.options.getStateSnapshot?.() || {};
    const elapsedMsSinceRecordingStarted = this.recordingStartedAt
      ? Math.max(0, this.now() - this.recordingStartedAt)
      : undefined;
    const payload = removeUndefined({
      eventType,
      occurredAt,
      sequence: sequenceNumber,
      pluginInstanceId: this.pluginInstanceId,
      iframeInstanceId: this.iframeInstanceId,
      source: SOURCE,
      stateMachineState: stateSnapshot.stateMachineState,
      recordingActive: this.recordingActive,
      recorderVisible: stateSnapshot.recorderVisible,
      iframePresent: stateSnapshot.iframePresent,
      iframeLoaded: stateSnapshot.iframeLoaded,
      iframeLoading: stateSnapshot.iframeLoading,
      iframeErrored: stateSnapshot.iframeErrored,
      documentVisibility: this.documentRef?.visibilityState,
      pagePath: sanitizeLifecyclePagePath(this.options.getPagePath?.() || this.windowRef?.location.pathname || "/"),
      elapsedMsSinceRecordingStarted,
      recentEventTypes: this.recentEvents.slice(-MAX_RECENT_EVENTS),
      ...overrides,
    }) as RecorderLifecyclePayload;

    return payload;
  }

  private nextSequence(): number {
    this.sequence += 1;
    this.storage?.setItem(SEQUENCE_KEY, String(this.sequence));
    return this.sequence;
  }

  private rememberRecentEvent(eventType: RecorderLifecycleEventType, occurredAt: string, occurredAtTime: number): void {
    this.recentEvents = [
      ...this.recentEvents,
      {
        eventType,
        occurredAt,
        ageMs: Math.max(0, this.now() - occurredAtTime),
      },
    ].slice(-MAX_RECENT_EVENTS);

    this.storage?.setItem(RECENT_EVENTS_KEY, JSON.stringify(this.recentEvents));
  }

  private persistRecordingState(): void {
    this.storage?.setItem(RECORDING_ACTIVE_KEY, String(this.recordingActive));
    this.storage?.setItem(RECORDING_STARTED_AT_KEY, String(this.recordingStartedAt || ""));
  }

  private stopRecordingTracking(): void {
    this.recordingActive = false;
    this.recordingStartedAt = 0;
    this.startedSource = null;
    this.storage?.setItem(RECORDING_ACTIVE_KEY, "false");
    this.storage?.removeItem(RECORDING_STARTED_AT_KEY);
    this.cleanupActiveLifecycleTracking();
  }

  private startActiveLifecycleTracking(): void {
    if (!this.windowRef || this.activeLifecycleCleanup) return;

    const historyRef = this.windowRef.history;
    const originalPushState = historyRef.pushState;
    const originalReplaceState = historyRef.replaceState;
    const trackNavigation = this.trackNavigation.bind(this);

    const wrapHistoryMethod = (methodName: MutableHistoryMethod, originalMethod: History[MutableHistoryMethod]) => {
      historyRef[methodName] = function patchedHistoryMethod(
        this: History,
        data: unknown,
        unused: string,
        url?: string | URL | null
      ) {
        const result = originalMethod.apply(this, [data, unused, url]);
        void trackNavigation(methodName);
        return result;
      } as History[MutableHistoryMethod];
    };

    const onPopState = () => void this.trackNavigation("popstate");
    const onHashChange = () => void this.trackNavigation("hashchange");
    const onVisibilityChange = () => void this.trackDocumentVisibility();
    const onPageHide = () => void this.trackNavigation("pagehide", { keepalive: true });
    const onBeforeUnload = () => void this.trackNavigation("beforeunload", { keepalive: true });

    wrapHistoryMethod("pushState", originalPushState);
    wrapHistoryMethod("replaceState", originalReplaceState);
    this.windowRef.addEventListener("popstate", onPopState);
    this.windowRef.addEventListener("hashchange", onHashChange);
    this.documentRef?.addEventListener("visibilitychange", onVisibilityChange);
    this.windowRef.addEventListener("pagehide", onPageHide);
    this.windowRef.addEventListener("beforeunload", onBeforeUnload);

    this.activeLifecycleCleanup = () => {
      historyRef.pushState = originalPushState;
      historyRef.replaceState = originalReplaceState;
      this.windowRef?.removeEventListener("popstate", onPopState);
      this.windowRef?.removeEventListener("hashchange", onHashChange);
      this.documentRef?.removeEventListener("visibilitychange", onVisibilityChange);
      this.windowRef?.removeEventListener("pagehide", onPageHide);
      this.windowRef?.removeEventListener("beforeunload", onBeforeUnload);
    };
  }

  private cleanupActiveLifecycleTracking(): void {
    this.activeLifecycleCleanup?.();
    this.activeLifecycleCleanup = null;
  }
}

function getDefaultStorage(): Storage | null {
  if (typeof sessionStorage === "undefined") return null;

  return sessionStorage;
}

function getOrCreateStoredValue(storage: Storage | null, key: string, createValue: () => string): string {
  const storedValue = storage?.getItem(key);
  if (storedValue) return storedValue;

  const value = createValue();
  storage?.setItem(key, value);
  return value;
}

function persistRecorderSessionId(storage: Storage | null, recorderSessionId: string): void {
  storage?.setItem(RECORDER_SESSION_ID_KEY, recorderSessionId);
}

function readRecentEvents(storage: Storage | null): RecentRecorderLifecycleEvent[] {
  const storedValue = storage?.getItem(RECENT_EVENTS_KEY);
  if (!storedValue) return [];

  try {
    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((event) => RECORDER_LIFECYCLE_EVENT_TYPES.includes(event.eventType));
  } catch {
    return [];
  }
}

function toNumber(value: string | null | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function createInstanceId(): string {
  const cryptoRef = typeof crypto !== "undefined" ? crypto : undefined;
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }

  return Math.random().toString(36).substring(2, 15);
}

function isIdentifierLike(segment: string): boolean {
  return (
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{24}$/i.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)
  );
}

function removeUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entryValue]) => entryValue !== undefined)) as T;
}
